import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { DEFAULT_PREFS, type Locale, type ReversalFrequency, type UserPrefs } from 'shared';
import { api } from '@/lib/api';
import { currentLocale, setLocale as applyLocale } from '@/i18n';
import { useAuthStore } from './auth';

/**
 * Preferences belong to the account and are read from it on sign-in. Signed
 * out (the sign-in gate) there is no account, so only the interface language
 * applies, and that is kept by i18n itself.
 */
export const usePrefsStore = defineStore('prefs', () => {
  const prefs = ref<UserPrefs>({ ...DEFAULT_PREFS, locale: currentLocale() });
  const loaded = ref(false);
  /**
   * Where the reader last was in each deck, so reopening it returns them
   * there. Session-only: it is not worth a server write per card swiped.
   */
  const lastCards = ref<Record<string, string>>({});

  const auth = useAuthStore();

  const reversalsEffective = computed(() =>
    auth.entitlements.configurableReversals ? prefs.value.reversalsEnabled : true,
  );

  const frequencyEffective = computed<ReversalFrequency>(() =>
    auth.entitlements.configurableReversals ? prefs.value.reversalFrequency : DEFAULT_PREFS.reversalFrequency,
  );

  async function hydrate(): Promise<void> {
    prefs.value = auth.user?.prefs
      ? { ...DEFAULT_PREFS, ...auth.user.prefs }
      : { ...DEFAULT_PREFS, locale: currentLocale() };

    applyLocale(prefs.value.locale);
    loaded.value = true;
  }

  // Signing out, or another account signing in, must not keep the previous
  // reader's settings or place in the deck.
  watch(
    () => auth.user?.id ?? null,
    () => {
      lastCards.value = {};
      void hydrate();
    },
  );

  async function update(patch: Partial<UserPrefs>): Promise<void> {
    prefs.value = { ...prefs.value, ...patch };

    if (patch.locale) applyLocale(patch.locale);

    if (!auth.isSignedIn) return;
    try {
      const data = await api<{ user: { prefs: UserPrefs } }>('/me/prefs', {
        method: 'PUT',
        body: patch,
      });
      // The server normalises what a free account is allowed to set, so take
      // its answer rather than assuming the patch applied as sent.
      prefs.value = { ...prefs.value, ...data.user.prefs };
    } catch {
      // Offline: the change holds for this session only.
    }
  }

  const setLocale = (locale: Locale): Promise<void> => update({ locale });

  const lastCardIn = (deckSlug: string): string | null => lastCards.value[deckSlug] ?? null;

  function setLastCard(deckSlug: string, cardId: string): void {
    lastCards.value = { ...lastCards.value, [deckSlug]: cardId };
  }

  return {
    prefs, loaded, reversalsEffective, frequencyEffective,
    hydrate, update, setLocale, lastCardIn, setLastCard,
  };
});
